import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  NzTableFilterFn,
  NzTableFilterList,
  NzTableModule,
  NzTableQueryParams,
  NzTableSortFn,
  NzTableSortOrder,
} from 'ng-zorro-antd/table';
import { NgForOf, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { NzBadgeComponent } from 'ng-zorro-antd/badge';
import { ActivatedRoute, Router } from '@angular/router';
import { ProvisionRequest } from '../../api/provision-request-api.class';
import { StoreService } from '../../store/store.service';
import { firstValueFrom, Observable } from 'rxjs';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { FormsModule } from '@angular/forms';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzInputDirective, NzInputGroupComponent } from 'ng-zorro-antd/input';
import { DateTime } from 'luxon';
import { NzSpinComponent } from 'ng-zorro-antd/spin';

interface ColumnItem {
  id: number;
  name: string;
  sortOrder: NzTableSortOrder | null;
  sortFn: NzTableSortFn<ProvisionRequest> | null;
  listOfFilter: NzTableFilterList;
  filterFn: NzTableFilterFn<ProvisionRequest> | null;
  showFilter: boolean;
}

@Component({
  selector: 'app-provisions-list',
  standalone: true,
  imports: [
    NzTableModule,
    NgForOf,
    NzBadgeComponent,
    NgSwitch,
    NgSwitchCase,
    NzEmptyModule,
    FormsModule,
    NzButtonComponent,
    NzIconDirective,
    NzInputDirective,
    NzInputGroupComponent,
    NgIf,
    NzSpinComponent,
  ],
  templateUrl: './provisions-list.component.html',
  styleUrl: './provisions-list.component.scss',
})
export class ProvisionsListComponent implements OnInit, AfterViewInit {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  provisionRequests$: Observable<ProvisionRequest[]>;
  displayedProvisionRequests: ProvisionRequest[] = [];
  protected readonly DateTime = DateTime;
  loading = true;
  searchValue = '';
  listOfColumns: ColumnItem[] = [
    {
      id: 0,
      name: 'Bezeichnung',
      sortOrder: null,
      sortFn: (a: ProvisionRequest, b: ProvisionRequest) =>
        a.bezeichnung.localeCompare(b.bezeichnung),
      listOfFilter: [],
      filterFn: null,
      showFilter: false,
    },
    {
      id: 1,
      name: 'Modulname',
      sortOrder: null,
      sortFn: (a: ProvisionRequest, b: ProvisionRequest) =>
        a.modul?.bezeichnung.localeCompare(b.modul?.bezeichnung),
      listOfFilter: [],
      filterFn: null,
      showFilter: false,
    },
    {
      id: 2,
      name: 'Ersteller',
      sortOrder: null,
      sortFn: (a: ProvisionRequest, b: ProvisionRequest) =>
        a.ersteller.name.localeCompare(b.ersteller.name),
      listOfFilter: [],
      filterFn: null,
      showFilter: false,
    },
    {
      id: 2,
      name: 'Zeitraum',
      sortOrder: null,
      sortFn: (a: ProvisionRequest, b: ProvisionRequest) =>
        DateTime.fromISO(a.datumVon).toMillis() -
        DateTime.fromISO(b.datumVon).toMillis(),
      listOfFilter: [],
      filterFn: null,
      showFilter: false,
    },
    {
      id: 3,
      name: 'Status',
      sortOrder: 'ascend',
      sortFn: (a: ProvisionRequest, b: ProvisionRequest) => {
        const order = ['Ausstehend', 'Aktiv', 'Ausgelaufen', 'Beendet'];
        return order.indexOf(a.status) - order.indexOf(b.status);
      },
      showFilter: true,
      listOfFilter: [
        { text: 'Aktiv', value: 'Aktiv' },
        { text: 'Beendet', value: 'Beendet' },
        { text: 'Ausstehend', value: 'Ausstehend' },
        { text: 'Ausgelaufen', value: 'Ausgelaufen' },
      ],
      filterFn: (filterValues: string[], item: ProvisionRequest) => {
        return filterValues.includes(item.status);
      },
    },
  ];
  private isRestoringTableState = false;
  tableState$ = this.store.select((state) => state.tables.provisionListTable);

  constructor(
    private router: Router,
    private store: StoreService,
    private route: ActivatedRoute,
  ) {
    this.provisionRequests$ = this.store.select(
      (state) => state.provisionRequests.activeProvisionRequests,
    );
  }

  async ngOnInit() {
    this.isRestoringTableState = true;
    this.loading = true;

    const tableState = await firstValueFrom(this.tableState$);

    if (tableState) {
      const {
        tables: { applyFiltersToColumns, applySortToColumns },
      } = this.store.getActions();

      applyFiltersToColumns(tableState, this.listOfColumns);
      applySortToColumns(tableState, this.listOfColumns);
    }

    this.route.queryParams.subscribe(async (params) => {
      const {
        provisionRequests: { setProvisionRequests },
      } = this.store.getActions();

      await setProvisionRequests(params);
      this.displayedProvisionRequests = await firstValueFrom(
        this.provisionRequests$,
      );
      this.isRestoringTableState = false;
      this.loading = false;
    });
  }

  ngAfterViewInit() {
    this.searchInput.nativeElement.focus();
  }

  onTableQueryParamsChange(params: NzTableQueryParams) {
    if (this.isRestoringTableState) {
      return;
    }

    const {
      tables: { buildTableStateFromQueryParams },
    } = this.store.getActions();

    const tableState = buildTableStateFromQueryParams(
      params,
      this.listOfColumns,
    );

    const {
      tables: { setProvisionsTableState },
    } = this.store.getActions();
    setProvisionsTableState(tableState);
  }

  onProvisionClick(data: ProvisionRequest) {
    const {
      provisionRequests: { updateActiveProvisionRequest },
    } = this.store.getActions();

    updateActiveProvisionRequest(data);
    this.router.navigate(['/provisions', data.id]);
  }

  async onSearch() {
    const requests = await firstValueFrom(this.provisionRequests$);
    const search = this.searchValue.trim().toLowerCase();

    if (!search) {
      this.displayedProvisionRequests = requests;
      return;
    }

    // Split search text into individual words
    const terms = search.split(/\s+/).filter(Boolean);

    this.displayedProvisionRequests = requests.filter((request) => {
      // Concatenate all searchable fields into one string
      const searchableText = [
        request.bezeichnung,
        request.modul?.bezeichnung,
        request.ersteller?.vorname,
        request.ersteller?.name,
        `${request.ersteller?.vorname ?? ''} ${request.ersteller?.name ?? ''}`,
        DateTime.fromISO(request.datumVon).toLocaleString(),
      ]
        .map((v) => v?.toLowerCase() ?? '')
        .join(' ');

      // Every term must appear somewhere in the combined text
      return terms.every((term) => searchableText.includes(term));
    });
  }
}
